import { Capacitor } from "@capacitor/core";
import { getSessionToken } from "./sessionToken";
import api from "./api";
import { offerKey, offerTerms, offerFingerprint, googleReplacementMode } from "./storeOffers";
import { deliverSubscription, recoverSubscriptions, runBillingOperation, assertSession } from "./iapDelivery";

export const PRODUCT_BY_PLAN = {
  starter: "io.hitmax.app.subscription.starter",
  pro: "io.hitmax.app.subscription.pro",
  vip: "io.hitmax.app.subscription.vip",
};
export const PRODUCT_IDS = Object.values(PRODUCT_BY_PLAN);
export const MATCH_UNLOCK_PRODUCT_ID = "hitmax_match_unlock";
export const APPLE_MATCH_UNLOCK_PRODUCT_ID = "io.hitmax.app.match_unlock";
const PLAN_RANK = { starter: 1, pro: 2, vip: 3 };

const planForProduct = (productId) =>
  Object.keys(PRODUCT_BY_PLAN).find((plan) => PRODUCT_BY_PLAN[plan] === productId);

export const nativeIapAvailable = () =>
  Capacitor.isNativePlatform() && ["ios", "android"].includes(Capacitor.getPlatform());

export const iosIapAvailable = () =>
  nativeIapAvailable() && Capacitor.getPlatform() === "ios";

async function plugin() {
  return await import("@capgo/native-purchases");
}

export function loadProducts() {
  return runBillingOperation(loadProductsImpl);
}

async function loadProductsImpl() {
  if (!nativeIapAvailable()) return [];
  const { NativePurchases, PURCHASE_TYPE } = await plugin();
  const sup = await NativePurchases.isBillingSupported();
  if (!sup?.isBillingSupported) throw new Error("Paiements natifs indisponibles");
  const res = await NativePurchases.getProducts({
    productIdentifiers: PRODUCT_IDS,
    productType: PURCHASE_TYPE.SUBS,
  });
  return res?.products || [];
}

export function storeProductId(product) {
  return product?.planIdentifier || product?.productIdentifier || product?.identifier;
}

async function googleAccountContext() {
  const session = getSessionToken();
  const { data } = await api.post("/iap/google/account", {}, { expectedSession: session, timeout: 30000 });
  const context = { session, accountId: data.account_id };
  assertGoogleSession(context);
  if (!context.accountId) throw new Error("Compte Google Play indisponible");
  return context;
}

async function appleAccountContext() {
  const session = getSessionToken();
  if (!session) throw new Error("Connecte-toi avant de souscrire");
  const { data } = await api.post("/iap/apple/account", {}, { expectedSession: session, timeout: 30000 });
  const context = { session, accountId: data.account_id };
  assertGoogleSession(context);
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(context.accountId || "")) {
    throw new Error("Identifiant de compte Apple indisponible");
  }
  return context;
}

function assertGoogleSession(context) {
  if (getSessionToken() !== context.session) {
    throw new Error("Le compte a changé. Reconnecte-toi au compte utilisé pour cet achat puis restaure l’abonnement.");
  }
}

export const STORE_NAMES = { apple: "Apple", google: "Google Play" };
export const STORE_MANAGEMENT_URLS = {
  apple: "https://apps.apple.com/account/subscriptions",
  google: "https://play.google.com/store/account/subscriptions",
};
export const currentStore = () => Capacitor.getPlatform() === "ios" ? "apple" : "google";
export const otherActiveSubscription = (subscriptions = []) =>
  subscriptions.find((s) => s.active && s.provider !== currentStore());

async function buyImpl(productIdentifier, storeProduct, currentPlan = "free") {
  const { NativePurchases, PURCHASE_TYPE } = await plugin();
  const isAndroid = Capacitor.getPlatform() === "android";
  const account = isAndroid ? await googleAccountContext() : await appleAccountContext();
  let replacement = {};
  let sourceBilling, upgrading;
  if (isAndroid) {
    const purchases = await NativePurchases.getPurchases({
      productType: PURCHASE_TYPE.SUBS,
      onlyCurrentEntitlements: true,
    });
    const currentPurchases = (purchases?.purchases || []).filter(
      (item) => item.purchaseToken && PRODUCT_IDS.includes(item.productIdentifier)
    );
    for (const currentPurchase of currentPurchases) {
      assertGoogleSession(account);
      if (String(currentPurchase.purchaseState) === "2") {
        return { active: false, pending: true };
      }
      // Verify ownership before opening a replacement sheet, including historical
      // purchases that have no local account identifier. Server data is authoritative.
      const { data: verified } = await api.post("/iap/google/subscriptions", {
        purchase_token: currentPurchase.purchaseToken,
        product_id: currentPurchase.productIdentifier,
      });
      assertGoogleSession(account);
      if (typeof verified?.active !== "boolean") throw new Error("Validation Google Play incomplète");
      if (!verified.active) continue;
      if (verified.pending_plan) throw new Error("Un changement de forfait est déjà programmé. Consulte la gestion des abonnements Google Play.");
      if (currentPurchase.productIdentifier === productIdentifier) {
        throw new Error("Ce forfait est déjà actif");
      }
      if (replacement.oldPurchaseToken) {
        throw new Error("Plusieurs abonnements Google Play actifs : ouvre la gestion des abonnements.");
      }
      const sourcePlan = verified.plan;
      if (!PLAN_RANK[sourcePlan]) throw new Error("Abonnement Google Play actuel introuvable");
      sourceBilling = verified.billing;
      upgrading = PLAN_RANK[planForProduct(productIdentifier)] > PLAN_RANK[sourcePlan];
      replacement = {
        oldPurchaseToken: currentPurchase.purchaseToken,
        previousProductId: currentPurchase.productIdentifier,
        accountId: verified.purchase_account_id || account.accountId,
      };
    }
    assertGoogleSession(account);
  }
  if (!isAndroid) {
    const existing = await NativePurchases.getPurchases({ onlyCurrentEntitlements: true });
    assertGoogleSession(account);
    if (!Array.isArray(existing?.purchases)) throw new Error("Vérification des achats Apple indisponible");
    for (const previous of existing.purchases) {
      if (!PRODUCT_IDS.includes(previous.productIdentifier)) continue;
      if (!previous.jwsRepresentation) throw new Error("Preuve d’achat Apple manquante. Restaure tes achats avant de souscrire.");
      assertGoogleSession(account);
      const { data: verified } = await api.post("/iap/apple/transactions", {
        signed_transaction: previous.jwsRepresentation,
      }, { expectedSession: account.session });
      assertGoogleSession(account);
      if (typeof verified?.active !== "boolean") throw new Error("Validation Apple incomplète");
      if (verified.active && previous.productIdentifier === productIdentifier) {
        throw new Error("Ce forfait est déjà actif");
      }
    }
  }
  // Re-read the authenticated account immediately before opening the store.
  // Failed status checks must not create a second subscription accidentally.
  const session = getSessionToken();
  const { data: status } = await api.get("/user/subscription");
  assertGoogleSession({ session });
  if (account) assertGoogleSession(account);
  const other = otherActiveSubscription(status.subscriptions);
  if (other) {
    throw new Error(`Un abonnement ${STORE_NAMES[other.provider]} est déjà actif. Gère ce forfait dans sa boutique pour éviter une double facturation.`);
  }
  // Re-query after ownership checks: an expired offer must never become another offer.
  const { products: catalog } = await NativePurchases.getProducts({
    productIdentifiers: PRODUCT_IDS, productType: PURCHASE_TYPE.SUBS,
  });
  assertGoogleSession(account);
  const fresh = catalog?.find((p) => storeProductId(p) === productIdentifier && offerKey(p) === offerKey(storeProduct));
  if (!fresh || !offerTerms(fresh) || offerFingerprint(fresh) !== offerFingerprint(storeProduct)) {
    throw new Error("Cette offre a changé ou n’est plus disponible. Recharge les offres avant de réessayer.");
  }
  if (replacement.oldPurchaseToken) {
    replacement.replacementMode = googleReplacementMode(sourceBilling, fresh, catalog, upgrading);
  }
  const tx = await NativePurchases.purchaseProduct({
    productIdentifier,
    productType: PURCHASE_TYPE.SUBS,
    quantity: 1,
    ...(isAndroid ? {
      // The plugin maps this to BillingFlowParams.setObfuscatedAccountId.
      appAccountToken: replacement.accountId || account.accountId,
      planIdentifier: fresh.identifier,
      offerToken: fresh.offerToken,
      autoAcknowledgePurchases: false,
      oldPurchaseToken: replacement.oldPurchaseToken,
      previousProductId: replacement.previousProductId,
      replacementMode: replacement.replacementMode,
    } : { appAccountToken: account.accountId, autoAcknowledgePurchases: false }),
  });

  assertGoogleSession(account);
  try {
    return await deliverSubscription({ ...tx, productIdentifier }, {
      ...account, provider: isAndroid ? "google" : "apple", native: NativePurchases,
    });
  } catch (error) {
    assertGoogleSession(account);
    // Only after the store has returned a purchase: a temporary validation
    // conflict is not another purchase to attempt. The durable queue retries it.
    if (error?.response?.status === 503) {
      window.dispatchEvent(new Event("hitmax-iap-retry"));
      return { active: false, activationPending: true };
    }
    throw error;
  }
}

export async function buy(...args) {
  const session = getSessionToken();
  return runBillingOperation(async () => {
    assertSession({ session });
    try { return await buyImpl(...args); }
    catch (error) {
      if (error?.code === "PAYMENT_PENDING") {
        window.dispatchEvent(new Event("hitmax-iap-retry"));
        return { active: false, pending: true };
      }
      if (error?.code === "PAYMENT_CANCELLED") return { active: false, cancelled: true };
      window.dispatchEvent(new Event("hitmax-iap-retry"));
      throw error;
    }
  });
}

let appleRestoreRunning = false;

async function restoreApple() {
  if (appleRestoreRunning) throw new Error("Une restauration est déjà en cours");
  const session = getSessionToken();
  if (!session) throw new Error("Connecte-toi pour restaurer tes achats");
  const context = { session };
  appleRestoreRunning = true;
  try {
    const { NativePurchases } = await plugin();
    const account = await appleAccountContext();
    assertGoogleSession(context);
    // User-initiated sync returns void. Read verified current entitlements next.
    await NativePurchases.restorePurchases();
    assertGoogleSession(context);
    const result = await NativePurchases.getPurchases({ onlyCurrentEntitlements: true });
    assertGoogleSession(context);
    if (!Array.isArray(result?.purchases)) throw new Error("Réponse Apple invalide, réessaie la restauration");
    let count = 0;
    const failures = [];
    const seen = new Set();
    for (const tx of result.purchases) {
      assertGoogleSession(context);
      if (!PRODUCT_IDS.includes(tx.productIdentifier)) continue;
      if (!tx.jwsRepresentation) {
        failures.push("Preuve d’achat Apple manquante, réessaie la restauration");
        continue;
      }
      if (seen.has(tx.jwsRepresentation)) continue;
      seen.add(tx.jwsRepresentation);
      try {
        const data = await deliverSubscription(tx, { ...account, provider: "apple", native: NativePurchases });
        assertGoogleSession(context);
        if (data.active) count++;
      } catch (error) {
        assertGoogleSession(context);
        failures.push(error?.response?.data?.detail || error?.message || "Restauration impossible");
      }
    }
    assertGoogleSession(context);
    if (failures.length) {
      throw new Error(count
        ? `Restauration partielle : ${count} abonnement(s) actif(s) retrouvé(s). ${failures[0]}`
        : failures[0]);
    }
    return count;
  } finally {
    appleRestoreRunning = false;
  }
}

async function restoreImpl() {
  if (!nativeIapAvailable()) throw new Error("Restauration native indisponible");
  if (iosIapAvailable()) return restoreApple();
  const { NativePurchases, PURCHASE_TYPE } = await plugin();
  const account = await googleAccountContext();
  const res = await NativePurchases.getPurchases({
    productType: PURCHASE_TYPE.SUBS,
    onlyCurrentEntitlements: true,
  });
  if (!Array.isArray(res?.purchases)) throw new Error("Réponse Google Play invalide, réessaie la restauration");
  const list = res.purchases;
  let count = 0;
  const failures = [];
  for (const tx of list) {
    try {
      if (tx.purchaseToken && PRODUCT_IDS.includes(tx.productIdentifier)) {
        assertGoogleSession(account);
        const { data } = await api.post("/iap/google/subscriptions", {
          purchase_token: tx.purchaseToken,
          product_id: tx.productIdentifier,
        }, { expectedSession: account.session });
        assertGoogleSession(account);
        if (typeof data?.active !== "boolean") throw new Error("Validation de l’achat incomplète");
        if (data.active) count++;
      }
    } catch (error) {
      failures.push(
        error?.response?.data?.detail || error?.message || "Restauration impossible"
      );
    }
  }
  assertGoogleSession(account);
  if (failures.length) throw new Error(count
    ? `Restauration partielle : ${count} abonnement(s) actif(s) retrouvé(s). ${failures[0]}` : failures[0]);
  return count;
}

let restoreRequested = false;
export async function restore() {
  if (restoreRequested) throw new Error("Une restauration est déjà en cours");
  const session = getSessionToken();
  if (!session) throw new Error("Connecte-toi pour restaurer tes achats");
  restoreRequested = true;
  try { return await runBillingOperation(() => { assertSession({ session }); return restoreImpl(); }); }
  catch (error) {
    window.dispatchEvent(new Event("hitmax-iap-retry"));
    throw error;
  } finally { restoreRequested = false; }
}

export async function manageNativeSubscriptions(provider = currentStore()) {
  if (!STORE_MANAGEMENT_URLS[provider]) throw new Error("Boutique inconnue");
  if (nativeIapAvailable() && provider === currentStore()) {
    const { NativePurchases } = await plugin();
    await NativePurchases.manageSubscriptions();
  } else {
    window.open(STORE_MANAGEMENT_URLS[provider], "_blank", "noopener,noreferrer");
  }
}

export async function startIapRecovery(onState) {
  if (!nativeIapAvailable()) return () => {};
  const session = getSessionToken();
  if (!session) return () => {};
  const { NativePurchases } = await plugin();
  const { App } = await import("@capacitor/app");
  const { Network } = await import("@capacitor/network");
  let stopped = false;
  let running = false;
  let requested = false;
  let foreground = true;
  let connected = true;
  let timer;
  let delay = 2000;
  let context;
  const handles = [];
  const schedule = (ms) => {
    clearTimeout(timer);
    if (!stopped && foreground && connected) timer = setTimeout(scan, ms);
  };
  async function scan() {
    if (stopped || !foreground || !connected || getSessionToken() !== session) return;
    if (running) { requested = true; return; }
    running = true;
    let retry = false;
    try {
      const result = await runBillingOperation(async () => {
        if (stopped) return null;
        assertSession({ session });
        if (!context) {
          const account = Capacitor.getPlatform() === "ios" ? await appleAccountContext() : await googleAccountContext();
          assertSession({ session });
          context = { ...account, provider: currentStore(), native: NativePurchases };
        }
        return recoverSubscriptions(context, context.provider === "apple"
          ? [...PRODUCT_IDS, APPLE_MATCH_UNLOCK_PRODUCT_ID] : PRODUCT_IDS);
      });
      if (stopped || !result) return;
      retry = result.retry || result.pending;
      onState(result);
      delay = retry ? Math.min(delay * 2, 60000) : 2000;
    } catch (_) {
      retry = true;
      if (!stopped) onState({ problem: "Synchronisation des achats interrompue. Nouvelle tentative automatique." });
      delay = Math.min(delay * 2, 60000);
    } finally {
      running = false;
      if (requested || retry) schedule(requested ? 500 : delay);
      requested = false;
    }
  }
  const wake = () => { delay = 2000; schedule(100); };
  try {
    handles.push(await NativePurchases.addListener("transactionUpdated", wake));
    handles.push(await App.addListener("appStateChange", ({ isActive }) => {
      foreground = isActive;
      if (isActive) wake(); else clearTimeout(timer);
    }));
    handles.push(await Network.addListener("networkStatusChange", (status) => {
      connected = status.connected;
      if (connected) wake(); else clearTimeout(timer);
    }));
  } catch (error) {
    handles.forEach((handle) => handle?.remove());
    throw error;
  }
  window.addEventListener("online", wake);
  window.addEventListener("hitmax-iap-retry", wake);
  scan();
  return () => {
    stopped = true;
    clearTimeout(timer);
    window.removeEventListener("online", wake);
    window.removeEventListener("hitmax-iap-retry", wake);
    handles.forEach((handle) => handle?.remove());
  };
}

export function buyMatchUnlock(matchId) {
  const session = getSessionToken();
  return runBillingOperation(() => { assertSession({ session }); return buyMatchUnlockImpl(matchId); });
}

export async function loadMatchUnlockProduct() {
  if (!nativeIapAvailable()) return null;
  const { NativePurchases, PURCHASE_TYPE } = await plugin();
  const id = iosIapAvailable() ? APPLE_MATCH_UNLOCK_PRODUCT_ID : MATCH_UNLOCK_PRODUCT_ID;
  const { products } = await NativePurchases.getProducts({ productIdentifiers: [id], productType: PURCHASE_TYPE.INAPP });
  return products?.find((p) => (p.productIdentifier || p.identifier) === id) || null;
}

async function buyAppleMatchUnlock(matchId) {
  const account = await appleAccountContext();
  const { NativePurchases, PURCHASE_TYPE } = await plugin();
  const context = { ...account, provider: "apple", native: NativePurchases };
  const recovery = await recoverSubscriptions(context, [APPLE_MATCH_UNLOCK_PRODUCT_ID]);
  if (recovery.retry) throw new Error("Un achat précédent est en cours d’activation. Réessaie dans quelques instants.");
  const { data: access } = await api.get("/user/free-trial-status", { params: { match_id: String(matchId) } });
  assertSession(account);
  if (access?.match_unlocked) return { unlocked: true, match_id: String(matchId) };
  const product = await loadMatchUnlockProduct();
  if (!product) throw new Error("Déblocage Apple indisponible pour le moment");
  assertSession(account);
  const { data } = await api.post("/iap/apple/match-intents", { match_id: String(matchId) }, { expectedSession: account.session });
  assertSession(account);
  const tx = await NativePurchases.purchaseProduct({
    productIdentifier: APPLE_MATCH_UNLOCK_PRODUCT_ID, productType: PURCHASE_TYPE.INAPP,
    appAccountToken: data.account_id, isConsumable: true, autoAcknowledgePurchases: false,
  });
  try {
    const result = await deliverSubscription({ ...tx, productIdentifier: APPLE_MATCH_UNLOCK_PRODUCT_ID },
      { ...account, provider: "apple", native: NativePurchases });
    if (!result.unlocked || result.match_id !== String(matchId)) throw new Error("Ce match n’a pas été débloqué");
    return result;
  } catch (error) {
    window.dispatchEvent(new Event("hitmax-iap-retry"));
    throw error;
  }
}

async function buyMatchUnlockImpl(matchId) {
  if (iosIapAvailable()) return buyAppleMatchUnlock(matchId);
  if (Capacitor.getPlatform() !== "android" || !Capacitor.isNativePlatform()) {
    throw new Error("Achat Google Play indisponible");
  }
  const { NativePurchases, PURCHASE_TYPE } = await plugin();
  const products = await NativePurchases.getProducts({
    productIdentifiers: [MATCH_UNLOCK_PRODUCT_ID],
    productType: PURCHASE_TYPE.INAPP,
  });
  const product = products?.products?.find(
    (p) => (p.productIdentifier || p.identifier) === MATCH_UNLOCK_PRODUCT_ID
  );
  if (!product) throw new Error("Produit Google Play indisponible");

  const tx = await NativePurchases.purchaseProduct({
    productIdentifier: MATCH_UNLOCK_PRODUCT_ID,
    productType: PURCHASE_TYPE.INAPP,
    offerToken: product.offerToken,
    isConsumable: false,
    autoAcknowledgePurchases: false,
  });
  if (!tx?.purchaseToken) throw new Error("Réponse Google Play invalide");
  const { data } = await api.post("/iap/google/match-unlocks", {
    purchase_token: tx.purchaseToken,
    product_id: MATCH_UNLOCK_PRODUCT_ID,
    match_id: String(matchId),
  });
  return data;
}


export function recoverDeletedAccountSubscription(code) {
  const session = getSessionToken();
  return runBillingOperation(async () => {
    assertSession({ session });
    if (!nativeIapAvailable()) throw new Error("Ouvre l’application mobile pour vérifier ton achat");
    const { NativePurchases, PURCHASE_TYPE } = await plugin();
    const apple = iosIapAvailable();
    if (apple) await NativePurchases.restorePurchases();
    assertSession({ session });
    const { purchases } = await NativePurchases.getPurchases({ productType: PURCHASE_TYPE.SUBS, onlyCurrentEntitlements: true });
    if (!Array.isArray(purchases)) throw new Error("Achats indisponibles");
    let count = 0;
    let failure;
    for (const tx of purchases) {
      if (!PRODUCT_IDS.includes(tx.productIdentifier) || String(tx.purchaseState) === "2") continue;
      assertSession({ session });
      try {
        const { data } = await api.post("/account/recover-subscription", {
          code, provider: apple ? "apple" : "google", product_id: tx.productIdentifier,
          ...(apple ? { signed_transaction: tx.jwsRepresentation } : { purchase_token: tx.purchaseToken }),
        }, { expectedSession: session });
        assertSession({ session });
        if (typeof data?.active !== "boolean") throw new Error("Récupération non confirmée");
        // Standard idempotent delivery finishes StoreKit only after server delivery.
        const context = apple ? await appleAccountContext() : await googleAccountContext();
        assertSession({ session });
        const delivered = await deliverSubscription(tx, { ...context, provider: apple ? "apple" : "google", native: NativePurchases });
        if (delivered.active) count++;
      } catch (error) { failure = error; }
    }
    assertSession({ session });
    if (failure) throw failure;
    if (!purchases.some((tx) => PRODUCT_IDS.includes(tx.productIdentifier))) throw new Error("Aucun abonnement trouvé dans cette boutique");
    return count;
  });
}
